import { inject } from '@angular/core';
import { SelectOptionSource, SelectOption, SelectItem } from '@siemens/element-ng/select';
import {
  Subject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  map,
  catchError
} from 'rxjs';

import { WorkspaceService } from '../../shared/services/workspace.service';

export class UserSelectSource implements SelectOptionSource<string> {
  private readonly workspaceService = inject(WorkspaceService);
  private readonly getExcludedOids?: () => Set<string>;

  /** Subject to handle search input changes and debounce the search requests */
  private searchTermSubject = new Subject<string>();
  private debouncedSearch$: Observable<SelectOption<string>[]>;

  constructor(getExcludedOids?: () => Set<string>) {
    this.getExcludedOids = getExcludedOids;
    // Initialize the debounced search observable
    this.debouncedSearch$ = this.searchTermSubject.pipe(
      debounceTime(500), // milliseconds
      distinctUntilChanged(), // ensures that the search term is only processed if it has changed
      switchMap(searchTerm => this.fetchOptions(searchTerm))
    );
  }

  /**
   * Cleanup method - completes observables
   * Called when the source is no longer needed
   */
  cleanup(): void {
    this.searchTermSubject.complete();
  }

  getOptionsForSearch(search: string): Observable<SelectItem<string>[]> {
    // This function is required if filtering is enabled.
    // It is never called with an empty search value.
    return new Observable<SelectItem<string>[]>(observer => {
      const subscription = this.debouncedSearch$.subscribe({
        next: options => {
          observer.next(options);
          observer.complete();
        },
        error: err => observer.error(err)
      });

      this.searchTermSubject.next(search);

      // unsubscribe function to clean up properly
      return () => subscription.unsubscribe();
    });
  }

  getOptionsForValues(values: string[]): Observable<SelectOption<string>[]> {
    // si-select will call this function when it needs to get options for the provided values.
    // This function is typically called when the value is set via a formControl or ngModel.
    return of([]); // Return empty array as we don't have specific options for values in this implementation
  }

  private fetchOptions(search: string): Observable<SelectOption<string>[]> {
    if (!search) return of([]);
    return this.workspaceService.searchUsers(search).pipe(
      map((users: any[]) => this.usersToSelectOptions(users)),
      catchError(error => {
        console.error('Error fetching users:', error);
        return of([]);
      })
    );
  }

  private usersToSelectOptions(users: any[]): SelectOption<string>[] {
    const excludedOids = this.getExcludedOids ? this.getExcludedOids() : new Set<string>();

    return users
      .map(user => ({
        value: user.oid,
        label: user.firstName + ' ' + user.lastName,
        type: 'option' as const
      }))
      .filter(option => !excludedOids.has(option.value));
  }
}
